import { useState } from 'react';
import { Lock, FileText, Check, AlertCircle, Loader2, Shield, Clock, CheckCircle } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { generatePartialPreviewHtml, formatDateFr } from '../utils/previewUtils';

const STRIPE_PAYMENT_LINK = "https://buy.stripe.com/3cI28q4NOcW936FcvOgjC04";

export default function AttestationHonneurPage() {
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    date_naissance: '',
    adresse: '',
    objet_attestation: '',
    lieu: '',
    date_document: new Date().toISOString().split('T')[0]
  });
  const [currentStep, setCurrentStep] = useState(1);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const handleInputChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const validateForm = () => {
    return formData.nom && formData.prenom && formData.adresse && 
           formData.objet_attestation && formData.lieu && formData.date_document;
  };
  
  const generatePreviewHtmlLocal = () => {
    const { nom, prenom, adresse, objet_attestation, lieu, date_document, date_naissance } = formData;
    
    let naissanceText = '';
    if (date_naissance) {
      const dn = new Date(date_naissance);
      naissanceText = `, né(e) le ${dn.getDate()}/${dn.getMonth()+1}/${dn.getFullYear()}`;
    }
    
    const introText = `Je soussigné(e) ${prenom} ${nom.toUpperCase()}${naissanceText}, demeurant à ${adresse.replace(/\n/g, ', ')}, atteste sur l'honneur`;
    
    return generatePartialPreviewHtml({
      nom,
      prenom,
      adresse,
      dateStr: formatDateFr(date_document),
      lieu,
      title: "ATTESTATION SUR L'HONNEUR",
      content: objet_attestation,
      introText
    });
  };
  
  const handleGeneratePreview = () => {
    if (!validateForm()) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setPreviewLoading(true);
    setError(null);
    
    setTimeout(() => {
      const html = generatePreviewHtmlLocal();
      setPreviewHtml(html);
      setCurrentStep(2);
      setPreviewLoading(false);
    }, 500);
  };
  
  const handlePayment = () => {
    setPaymentLoading(true);
    
    const paymentData = {
      documentId: 'attestation-honneur',
      documentName: "Attestation sur l'honneur",
      formData: formData,
      previewHtml: previewHtml,
      timestamp: Date.now()
    };
    localStorage.setItem('pdf_facile_payment', JSON.stringify(paymentData));
    
    window.location.href = STRIPE_PAYMENT_LINK;
  };
  
  return (
    <>
      <Helmet>
        <title>Attestation sur l'honneur PDF – Téléchargement rapide | PDF Facile</title>
        <meta name="description" content="Créez votre attestation sur l'honneur en ligne. Document conforme aux usages administratifs français, prêt en 2 minutes, sans inscription. Aperçu gratuit, téléchargement PDF à 4,99€." />
        <link rel="canonical" href="/attestation-sur-honneur-pdf" />
      </Helmet>
      
      <div className="min-h-screen flex flex-col bg-white">
        <Header showBack backTo="/" />
        
        <main className="flex-1">
          {/* Hero Section */}
          <section className="bg-slate-50 py-8 md:py-12">
            <div className="container-app max-w-3xl">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary mb-4" data-testid="attestation-h1">
                Attestation sur l'honneur à télécharger en PDF
              </h1>
              <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
                Générez une attestation sur l'honneur <strong>conforme aux usages administratifs français</strong>. 
                Votre document est prêt en quelques minutes, sans aucune inscription requise. 
                Visualisez gratuitement l'aperçu partiel avant de télécharger votre PDF.
              </p>
              
              {/* Trust badges */}
              <div className="flex flex-wrap gap-4 mt-6 text-sm text-slate-600">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-green-600" />
                  <span>Document conforme</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-600" />
                  <span>Prêt en 2 min</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>Sans inscription</span>
                </div>
              </div>
            </div>
          </section>
          
          {/* Step Indicator */}
          <section className="py-4 border-b border-slate-200">
            <div className="container-app max-w-3xl">
              <div className="flex items-center justify-center gap-2 sm:gap-4">
                <div className="flex items-center gap-2">
                  <div className={`step-dot ${currentStep >= 1 ? (currentStep > 1 ? 'completed' : 'active') : 'inactive'}`}>
                    {currentStep > 1 ? <Check className="w-4 h-4" /> : '1'}
                  </div>
                  <span className="text-sm font-medium hidden sm:inline">Formulaire</span>
                </div>
                <div className={`step-line flex-1 max-w-[60px] ${currentStep > 1 ? 'completed' : ''}`} />
                <div className="flex items-center gap-2">
                  <div className={`step-dot ${currentStep >= 2 ? 'active' : 'inactive'}`}>
                    2
                  </div>
                  <span className="text-sm font-medium hidden sm:inline">Aperçu & Téléchargement</span>
                </div>
              </div>
            </div>
          </section>
          
          {/* Form / Preview Section */}
          <section className="section-spacing">
            <div className="container-app max-w-2xl">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}
              
              {/* Step 1: Form */}
              {currentStep === 1 && (
                <div data-testid="attestation-form-step">
                  <h2 className="text-lg font-semibold mb-6">Complétez vos informations</h2>
                  
                  <div className="space-y-5">
                    {/* Nom */}
                    <div className="form-field">
                      <Label htmlFor="nom" className="label-field">
                        Nom <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="nom"
                        type="text"
                        placeholder="Dupont"
                        value={formData.nom}
                        onChange={(e) => handleInputChange('nom', e.target.value)}
                        className="input-field"
                        data-testid="field-nom"
                      />
                    </div>
                    
                    {/* Prénom */}
                    <div className="form-field">
                      <Label htmlFor="prenom" className="label-field">
                        Prénom <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="prenom"
                        type="text"
                        placeholder="Jean"
                        value={formData.prenom}
                        onChange={(e) => handleInputChange('prenom', e.target.value)}
                        className="input-field"
                        data-testid="field-prenom"
                      />
                    </div>
                    
                    {/* Date de naissance (optionnel) */}
                    <div className="form-field">
                      <Label htmlFor="date_naissance" className="label-field">
                        Date de naissance <span className="text-slate-400 text-xs">(optionnel)</span>
                      </Label>
                      <Input
                        id="date_naissance"
                        type="date"
                        value={formData.date_naissance}
                        onChange={(e) => handleInputChange('date_naissance', e.target.value)}
                        className="input-field"
                        data-testid="field-date-naissance"
                      />
                    </div>
                    
                    {/* Adresse */}
                    <div className="form-field">
                      <Label htmlFor="adresse" className="label-field">
                        Adresse <span className="text-red-500">*</span>
                      </Label>
                      <Textarea
                        id="adresse"
                        placeholder="123 rue de la Paix, 75001 Paris"
                        value={formData.adresse}
                        onChange={(e) => handleInputChange('adresse', e.target.value)}
                        className="input-field min-h-[80px] py-3"
                        data-testid="field-adresse"
                      />
                    </div>
                    
                    {/* Objet de l'attestation */}
                    <div className="form-field">
                      <Label htmlFor="objet_attestation" className="label-field">
                        Objet de l'attestation <span className="text-red-500">*</span>
                      </Label>
                      <Textarea
                        id="objet_attestation"
                        placeholder="Je soussigné(e) atteste sur l'honneur que..."
                        value={formData.objet_attestation}
                        onChange={(e) => handleInputChange('objet_attestation', e.target.value)}
                        className="input-field min-h-[120px] py-3"
                        data-testid="field-objet"
                      />
                    </div>
                    
                    {/* Lieu et Date sur la même ligne */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="form-field">
                        <Label htmlFor="lieu" className="label-field">
                          Lieu <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="lieu"
                          type="text"
                          placeholder="Paris"
                          value={formData.lieu}
                          onChange={(e) => handleInputChange('lieu', e.target.value)}
                          className="input-field"
                          data-testid="field-lieu"
                        />
                      </div>
                      
                      <div className="form-field">
                        <Label htmlFor="date_document" className="label-field">
                          Date <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="date_document"
                          type="date"
                          value={formData.date_document}
                          onChange={(e) => handleInputChange('date_document', e.target.value)}
                          className="input-field"
                          data-testid="field-date"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-8">
                    <Button
                      onClick={handleGeneratePreview}
                      disabled={!validateForm() || previewLoading}
                      className="btn-primary w-full"
                      data-testid="generate-preview-btn"
                    >
                      {previewLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Génération en cours...
                        </>
                      ) : (
                        <>
                          <FileText className="w-5 h-5 mr-2" />
                          Générer l'aperçu gratuitement
                        </>
                      )}
                    </Button>
                    
                    {/* Réassurance sous le bouton */}
                    <div className="mt-6 text-xs text-slate-500 space-y-1">
                      <p>• Documents conformes aux usages administratifs français</p>
                      <p>• Aperçu partiel gratuit avant paiement</p>
                      <p>• Téléchargement immédiat après paiement</p>
                      <p>• Aucune inscription requise</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Step 2: Preview */}
              {currentStep === 2 && previewHtml && (
                <div data-testid="attestation-preview-step">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-semibold">Aperçu de votre attestation</h2>
                    <button
                      onClick={() => setCurrentStep(1)}
                      className="text-sm text-accent hover:underline"
                    >
                      Modifier
                    </button>
                  </div>
                  
                  {/* Preview */}
                  <div 
                    className="preview-document mb-6"
                    dangerouslySetInnerHTML={{ __html: previewHtml }}
                  />
                  
                  {/* Payment CTA */}
                  <div className="bg-slate-50 rounded-lg p-6 text-center">
                    <div className="payment-badge inline-flex mb-4">
                      <Lock className="w-4 h-4" />
                      <span>Paiement sécurisé</span>
                    </div>
                    
                    <h3 className="text-lg font-semibold mb-2">
                      Votre attestation est prête
                    </h3>
                    <p className="text-muted-foreground text-sm mb-6">
                      Le PDF sera identique à l'aperçu ci-dessus, au format A4.
                    </p>
                    
                    <Button
                      onClick={handlePayment}
                      disabled={paymentLoading}
                      className="btn-primary w-full"
                      data-testid="pay-button"
                    >
                      {paymentLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Redirection...
                        </>
                      ) : (
                        <>
                          <Lock className="w-5 h-5 mr-2" />
                          Télécharger le PDF – 4,99 €
                        </>
                      )}
                    </Button>
                    
                    <p className="text-xs text-muted-foreground mt-4">
                      Paiement unique • Sans abonnement • Téléchargement immédiat
                    </p>
                    
                    <p className="text-xs text-slate-400 mt-3">
                      Paiement sécurisé via Stripe – transactions protégées par chiffrement SSL
                    </p>
                  </div>
                </div>
              )}
            </div>
          </section>
          
          {/* SEO Content Section */}
          <section className="bg-slate-50 section-spacing">
            <div className="container-app max-w-3xl">
              <h2 className="text-xl font-semibold mb-4">
                Qu'est-ce qu'une attestation sur l'honneur ?
              </h2>
              <div className="prose prose-slate max-w-none text-muted-foreground">
                <p>
                  L'attestation sur l'honneur est un document écrit par lequel une personne déclare, 
                  sous sa propre responsabilité, qu'une information ou un fait est véridique. 
                  Ce type de document est couramment utilisé dans les démarches administratives françaises 
                  lorsqu'aucun justificatif officiel n'est disponible ou exigé.
                </p>
                <p>
                  Elle peut servir à attester de nombreuses situations : la non-perception d'indemnités, 
                  la perte d'un document, une situation familiale, la conformité d'un logement, 
                  ou tout autre fait que vous devez déclarer officiellement.
                </p>
                <p>
                  <strong>Important :</strong> Une fausse déclaration sur l'honneur constitue un délit 
                  passible de sanctions pénales (article 441-7 du Code pénal). L'auteur de l'attestation 
                  engage sa responsabilité sur l'exactitude des informations fournies.
                </p>
                <p>
                  Notre modèle d'attestation sur l'honneur respecte les usages administratifs français 
                  et peut être utilisé pour la plupart des démarches courantes. Le document généré 
                  inclut tous les éléments nécessaires : identité du déclarant, objet de l'attestation, 
                  date, lieu et espace pour la signature.
                </p>
              </div>
            </div>
          </section>
        </main>
        
        <Footer />
      </div>
    </>
  );
}
